import React, { useRef, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { FaArrowLeft, FaDownload, FaCheckCircle, FaHourglassHalf, FaTimesCircle } from "react-icons/fa";
import DashboardNavbar from "../components/layout/DashboardNavbar";
import { MyInvoices } from "../services/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoImg from "../assets/logo2.png";

export default function WalletTransactionReceipt() {
  const navigate = useNavigate();
  const location = useLocation();
  const { transaction } = location.state || {};

  useEffect(() => {
    if (!transaction) navigate("/dashboard", { replace: true });
  }, [transaction, navigate]);

  const isDeposit = transaction?.transaction_type === "credit" || transaction?.type === "deposit";
  // For service payments, extract UCH invoice ref from description (e.g. "Invoice UCH-750401")
  const invoiceRef = transaction && !isDeposit && transaction.description
    ? (transaction.description.match(/UCH-\w+/)?.[0] || null)
    : null;

  const { data: invoices = [] } = useQuery({
    queryKey: ["my-invoices-receipt"],
    queryFn: MyInvoices,
    enabled: !!invoiceRef,
  });

  const matchedInvoice = invoiceRef
    ? invoices.find((inv) => inv.reference === invoiceRef || inv.id === invoiceRef)
    : null;
  const patientMeta = matchedInvoice?.customer_meta || null;
  const patientName = patientMeta
    ? `${patientMeta.patient_first_name || ""} ${patientMeta.patient_last_name || ""}`.trim()
    : "";
  const patientId = patientMeta?.patient_hospital_number || "";

  if (!transaction) return null;

  const amount = Number(transaction.rawAmount || transaction.amount || 0);
  const reference = invoiceRef || transaction.reference || transaction.id || "—";
  const rrr = transaction.rrr || "—";
  const date = transaction.date || (transaction.created_at ? new Date(transaction.created_at).toLocaleDateString("en-GB") : "—");
  const time = transaction.time || (transaction.created_at ? new Date(transaction.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");
  const statusVal = transaction.status || "unknown";

  const statusConfig = {
    success: { color: "text-green-600", bg: "bg-green-50 border-green-200", icon: FaCheckCircle, label: "Successful" },
    pending: { color: "text-yellow-600", bg: "bg-yellow-50 border-yellow-200", icon: FaHourglassHalf, label: "Pending" },
    failed:  { color: "text-red-600",   bg: "bg-red-50 border-red-200",     icon: FaTimesCircle,  label: "Failed" },
  };
  const sc = statusConfig[statusVal] || statusConfig.pending;
  const StatusIcon = sc.icon;

  // ─── Modern fintech PDF ────────────────────────────────────────────────────
  const handleDownloadPDF = async () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();

    // ── Watermark (faint logo) ──
    try {
      const img = new Image();
      img.src = logoImg;
      await new Promise((res) => { img.onload = res; img.onerror = res; });
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.06 }));
      doc.addImage(img, "PNG", W / 2 - 40, H / 2 - 40, 80, 80);
      doc.restoreGraphicsState();
    } catch (_) {}

    // ── Header bar ──
    doc.setFillColor(22, 101, 52); // green-800
    doc.rect(0, 0, W, 28, "F");
    try {
      const img = new Image(); img.src = logoImg;
      await new Promise((res) => { img.onload = res; img.onerror = res; });
      doc.addImage(img, "PNG", 8, 4, 20, 20);
    } catch (_) {}
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text("UCH SELF SERVICE", 32, 13);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("University College Hospital, Ibadan — Digital Payment Platform", 32, 20);

    // ── Receipt title ──
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(22, 101, 52);
    doc.text("WALLET TRANSACTION RECEIPT", W / 2, 42, { align: "center" });

    doc.setDrawColor(22, 101, 52);
    doc.setLineWidth(0.5);
    doc.line(14, 46, W - 14, 46);

    // ── Status badge ──
    const statusColor = statusVal === "success" ? [22, 163, 74] : statusVal === "pending" ? [202, 138, 4] : [220, 38, 38];
    doc.setFillColor(...statusColor);
    doc.roundedRect(W / 2 - 22, 50, 44, 9, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text(sc.label.toUpperCase(), W / 2, 56, { align: "center" });

    // ── Amount ──
    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    doc.setTextColor(22, 101, 52);
    doc.text(`NGN ${amount.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`, W / 2, 75, { align: "center" });
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(isDeposit ? "WALLET DEPOSIT (CREDIT)" : "WALLET PAYMENT (DEBIT)", W / 2, 82, { align: "center" });

    // ── Details table ──
    const rows = [
      ...(patientName ? [["Patient Name", patientName]] : []),
      ...(patientId ? [["Patient ID", patientId]] : []),
      ["Transaction Reference", reference],
      ["RRR (Remita)", rrr !== "—" ? rrr : "N/A"],
      ["Date", date],
      ["Time", time],
      ["Transaction Type", isDeposit ? "Wallet Deposit (Credit)" : "Wallet Payment (Debit)"],
      ["Status", sc.label],
      ["Description", transaction.description || (isDeposit ? "Wallet Top-up" : "Service Payment")],
    ];

    autoTable(doc, {
      startY: 90,
      margin: { left: 14, right: 14 },
      head: [["Field", "Details"]],
      body: rows,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 4, valign: "middle" },
      headStyles: { fillColor: [22, 101, 52], textColor: 255, fontStyle: "bold", fontSize: 9 },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 55, fillColor: [240, 253, 244], textColor: [22, 101, 52] },
        1: { cellWidth: "auto", fontStyle: "normal" },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });

    const finalY = (doc.lastAutoTable?.finalY ?? 150) + 10;

    // ── Next steps (for deposits only) ──
    if (!isDeposit && statusVal === "success") {
      doc.setFillColor(240, 253, 244);
      doc.setDrawColor(134, 239, 172);
      doc.roundedRect(14, finalY, W - 28, 42, 3, 3, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(22, 101, 52);
      doc.text("NEXT STEPS", 20, finalY + 7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(40, 40, 40);
      const steps = [
        "1. Proceed to any UCH Pay1One payment booth in the hospital.",
        `2. Tell the teller you are a VIP Payer. Present Reference: ${reference}`,
        "3. Teller will issue an official hospital receipt upon verification.",
        "4. Go to the relevant department to receive your service.",
      ];
      steps.forEach((s, i) => doc.text(s, 20, finalY + 14 + i * 7));
    }

    // ── Footer ──
    const footerY = H - 18;
    doc.setDrawColor(22, 101, 52);
    doc.setLineWidth(0.3);
    doc.line(14, footerY - 3, W - 14, footerY - 3);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(120, 120, 120);
    doc.text("This is a computer-generated receipt and does not require a signature.", W / 2, footerY + 2, { align: "center" });
    doc.text("UCH Self Service  |  support@uchselfservice.net  |  uchselfservice.net", W / 2, footerY + 7, { align: "center" });

    // ── QR-style reference box ──
    doc.setFillColor(22, 101, 52);
    doc.roundedRect(W / 2 - 35, footerY - 16, 70, 10, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(`REF: ${reference}`, W / 2, footerY - 9, { align: "center" });

    doc.save(`UCH_Receipt_${reference}_${date.replace(/\//g, "-")}.pdf`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <DashboardNavbar />
      <main className="max-w-lg mx-auto px-4 py-10 w-full">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-100 shadow-sm">
            <FaArrowLeft className="text-gray-600 w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Transaction Receipt</h1>
            <p className="text-xs text-gray-500 mt-0.5">UCH Self Service Wallet</p>
          </div>
        </div>

        {/* Receipt Card */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100">

          {/* Green header */}
          <div className="bg-gradient-to-r from-green-700 to-green-800 px-6 py-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logoImg} alt="UCH" className="w-10 h-10 rounded-xl border-2 border-white/30" />
              <div>
                <p className="text-white font-bold text-sm">UCH Self Service</p>
                <p className="text-green-200 text-xs">University College Hospital, Ibadan</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-green-200 text-xs">Amount</p>
              <p className="text-white font-bold text-2xl">₦{amount.toLocaleString("en-NG", { minimumFractionDigits: 2 })}</p>
            </div>
          </div>

          {/* Status */}
          <div className={`px-6 py-3 flex items-center gap-2 border-b ${sc.bg} border`}>
            <StatusIcon className={`w-4 h-4 ${sc.color}`} />
            <span className={`text-sm font-semibold ${sc.color}`}>{sc.label}</span>
            <span className="text-gray-400 text-xs ml-auto">{isDeposit ? "Credit · Deposit" : "Debit · Payment"}</span>
          </div>

          {/* Details */}
          <div className="px-6 py-5 space-y-3 text-sm">
            {[
              ...(patientName ? [["Patient Name", patientName]] : []),
              ...(patientId ? [["Patient ID", patientId]] : []),
              ["Reference", reference],
              ["RRR (Remita)", rrr !== "—" ? rrr : "—"],
              ["Date & Time", `${date} · ${time}`],
              ["Type", isDeposit ? "Wallet Deposit" : "Wallet Payment"],
              ["Description", transaction.description || (isDeposit ? "Wallet Top-up" : "Service Payment")],
            ].map(([label, val]) => (
              <div key={label} className="flex justify-between items-start gap-4">
                <span className="text-gray-500 shrink-0 font-medium">{label}</span>
                <span className="text-gray-900 font-semibold text-right break-all">{val}</span>
              </div>
            ))}
          </div>

          {/* Next steps for successful payments */}
          {!isDeposit && statusVal === "success" && (
            <div className="mx-6 mb-5 bg-green-50 border border-green-100 rounded-xl p-4 space-y-2">
              <p className="text-xs font-bold text-green-800 uppercase tracking-wide">Next Steps</p>
              {[
                "Proceed to any UCH Pay1One payment booth within the hospital premises.",
                `Tell the teller you are a VIP Payer and present your Reference: ${reference}. One-time use only.`,
                "The teller will issue you an official hospital receipt upon verification.",
                "Proceed to the relevant department or ward to receive your hospital service.",
              ].map((s, i) => (
                <div key={i} className="flex gap-2 text-xs text-gray-700">
                  <span className="w-4 h-4 bg-green-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">{i + 1}</span>
                  <p>{s}</p>
                </div>
              ))}
            </div>
          )}

          {/* Footer note */}
          <div className="px-6 py-3 bg-gray-50 border-t text-xs text-center text-gray-400">
            Computer-generated receipt · UCH Self Service · support@uchselfservice.net
          </div>
        </div>

        {/* Action: PDF only, no print */}
        <Button
          onClick={handleDownloadPDF}
          className="w-full mt-5 bg-green-700 hover:bg-green-800 text-white rounded-xl h-12 font-semibold flex items-center justify-center gap-2"
        >
          <FaDownload className="w-4 h-4" /> Download PDF Receipt
        </Button>

        <Button variant="outline" onClick={() => navigate("/dashboard")} className="w-full mt-3 rounded-xl h-11">
          Back to Dashboard
        </Button>
      </main>
    </div>
  );
}
